/**
 * Hand-written wrapper for the Calculator v3 Service Catalog endpoints.
 * Mirrors what the BE returns from /service-catalog. Collapses to the
 * generated client once it lands.
 */
import axiosInstance from './axios';

export type ServiceClassification = 'CONTRACT' | 'AD_HOC' | 'PROJECT';
export type ServiceComputeMode = 'SQFT_DRIVEN' | 'HOURS_DRIVEN';

export interface EffectiveTenantService {
  baseRateSqftPerHour: number;
  classification: ServiceClassification;
  code: string;
  computeMode: ServiceComputeMode;
  hasTenantRow: boolean;
  name: string;
  notes?: string;
  referenceDefaultRate?: number;
  referenceServiceId?: string;
  sortOrder: number;
  tenantRowId?: string;
}

const list = async (
  classification?: ServiceClassification
): Promise<EffectiveTenantService[]> => {
  const { data } = await axiosInstance.get<EffectiveTenantService[]>(
    '/service-catalog',
    classification ? { params: { classification } } : undefined
  );
  return data;
};

const upsertOverride = async (payload: {
  name?: string;
  notes?: string;
  rateSqftPerHour?: number | null;
  referenceServiceId: string;
}): Promise<EffectiveTenantService[]> => {
  const { data } = await axiosInstance.post<EffectiveTenantService[]>(
    '/service-catalog/override',
    payload
  );
  return data;
};

const createAddition = async (payload: {
  name: string;
  notes?: string;
  rateSqftPerHour: number;
}): Promise<EffectiveTenantService[]> => {
  const { data } = await axiosInstance.post<EffectiveTenantService[]>(
    '/service-catalog/addition',
    payload
  );
  return data;
};

const updateTenantRow = async (
  rowId: string,
  patch: { name?: string; notes?: string; rateSqftPerHour?: number | null }
): Promise<EffectiveTenantService[]> => {
  const { data } = await axiosInstance.put<EffectiveTenantService[]>(
    `/service-catalog/tenant/${rowId}`,
    patch
  );
  return data;
};

const deleteTenantRow = async (
  rowId: string
): Promise<EffectiveTenantService[]> => {
  const { data } = await axiosInstance.delete<EffectiveTenantService[]>(
    `/service-catalog/tenant/${rowId}`
  );
  return data;
};

const serviceCatalogApi = {
  createAddition,
  deleteTenantRow,
  list,
  updateTenantRow,
  upsertOverride
};

export default serviceCatalogApi;
